import { Pressable, ScrollView, Text, View, Platform } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { useMutation } from '@tanstack/react-query';
import { Query } from '@/utils/react-query/Queries';
import { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import SheetSelector from '@/components/General/inputs/SheetSelector';
import SectionTitle from '@/components/General/SectionTitle';
import Unicon from '@/components/General/Unicon';
import { ActivityIndicator } from 'react-native-paper';
import { DateTime } from 'luxon';
import Toast from 'react-native-toast-message';
import { mergePeople, ignorePotentialDuplicate } from '@/data/people';
import useLoader from '@/utils/hooks/useLoader';
import { queryClient } from '@/data/client';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import { showAlert } from '@/utils/showAlert';
import { useNavigation, usePathname } from 'expo-router';
import SolidButton from '@/components/General/SolidButton';
import Callout from '@/components/General/Callout';
import Avatar from '@/components/People/Avatar';
import DuplicatePairCard from '@/components/People/DuplicatePairCard';
import useOrganizationEntitlements from '@/utils/hooks/useOrganizationEntitlements';
import { isFreeOrg } from '@/utils/organizationEntitlements';

const STEP_SELECT = 0;
const STEP_FIELDS = 1;
const STEP_RELATIONS = 2;
const STEP_REVIEW = 3;

function formatMergeDate(value, includeTime = false) {
    let date = typeof value === 'string' ? DateTime.fromISO(value.trim()) : DateTime.fromJSDate(value);
    if (!date.isValid && typeof value === 'string') {
        date = DateTime.fromSQL(value.trim());
        if (!date.isValid) date = DateTime.fromFormat(value.trim(), 'MM/dd/yyyy');
    }
    return date.isValid
        ? date.toLocaleString(includeTime ? DateTime.DATETIME_MED : DateTime.DATE_MED)
        : String(value);
}

function InputSection({ title, description, children }) {
    return (
        <View style={styles.section}>
            <View style={styles.sectionHeader}>
                <Text style={styles.sectionTitle}>{title}</Text>
                {!!description && <Text style={styles.sectionDescription}>{description}</Text>}
            </View>
            <View style={styles.sectionBody}>{children}</View>
        </View>
    );
}

const BASIC_FIELDS = {
    "firstName": { label: "First Name", icon: "person-outline" },
    "lastName": { label: "Last Name", icon: "person-outline" },
    "phone": { label: "Phone", icon: "call-outline" },
    "gender": { label: "Gender", icon: "male-female-outline" },
    "email": { label: "Email", icon: "mail-outline" },
    "dateOfBirth": { label: "Date of Birth", icon: "calendar-outline" },
    "InvitedBy": { label: "Invited By", icon: "people-outline" },
    "Address": { label: "Address", icon: "location-outline" },
};

const RELATION_TABLES = {
    "Attendance": {
        label: "Attendance Records",
        icon: "checkmark-done-outline",
        description: "Event check-in records",
        allowBoth: true
    },
    "TaskInstancePeople": {
        label: "Task Assignments",
        icon: "checkbox-outline",
        description: "Tasks assigned to this person",
        allowBoth: false
    },
    "SmallGroupsLed": {
        label: "Small Groups Led",
        icon: "people-circle-outline",
        description: "Small groups where this person is the leader",
        allowBoth: true
    },
    "PersonConversations": {
        label: "Conversations",
        icon: "chatbubbles-outline",
        description: "Message conversations",
        allowBoth: false
    },
    "RolePeople": {
        label: "Role Assignments",
        icon: "shield-outline",
        description: "Roles assigned to this person",
        allowBoth: false
    },
    "BlackoutPeriods": {
        label: "Blackout Periods",
        icon: "calendar-clear-outline",
        description: "Scheduling blackout dates",
        allowBoth: true
    }
};

export default function MergePeopleScreen() {
    const { data: loggedInUser, isPending, isError, refetch } = Query.people.loggedInUser();
    const navigation = useNavigation();
    const canMerge = !!(loggedInUser?.isAdmin || loggedInUser?.can?.('Edit People'));

    useEffect(() => {
        if (!canMerge) navigation.setOptions({ headerRight: () => null });
    }, [canMerge, navigation]);

    if (isPending) {
        return <View style={styles.loadingContainer} testID="merge-permissions-loading"><ActivityIndicator size="large" /></View>;
    }
    if (isError) {
        return (
            <View style={styles.loadingContainer} testID="merge-permissions-error">
                <Callout type="danger" message="Could not check your merge permissions. Please try again." />
                <SolidButton text="Try again" onPress={refetch} />
            </View>
        );
    }
    if (!canMerge) {
        return (
            <View style={styles.loadingContainer} testID="merge-permission-denied">
                <Callout type="warning" title="Permission required" message="You need Edit People permission to merge people or review duplicate suggestions." />
            </View>
        );
    }
    return <MergePeopleWizard />;
}

function MergePeopleWizard() {
    const [currentStep, setCurrentStep] = useState(STEP_SELECT);
    const [selectedFirstPerson, setSelectedFirstPerson] = useState(null);
    const [selectedSecondPerson, setSelectedSecondPerson] = useState(null);
    const [primaryPersonId, setPrimaryPersonId] = useState(null);
    const [fieldSelections, setFieldSelections] = useState({});
    const [relationSelections, setRelationSelections] = useState({});

    const scrollRef = useRef(null);
    const loader = useLoader();
    const navigation = useNavigation();
    const pathname = usePathname();

    const { entitlements } = useOrganizationEntitlements();
    // Custom fields are paid-only — Free merges are scalar-fields only.
    const isFree = isFreeOrg(entitlements);
    const {
        data: potentialDuplicates, isLoading: loadingDuplicates,
        isError: duplicatesError, refetch: refetchDuplicates,
    } = Query.people.potentialDuplicates();
    const { data: customFields } = Query.personCustomFields.all({ options: { enabled: !isFree } });
    const { data: firstPerson, isLoading: loadingFirstPerson } = Query.people.byIdForMerge({
        id: selectedFirstPerson?.id,
        options: { enabled: selectedFirstPerson != null }
    });
    const { data: secondPerson, isLoading: loadingSecondPerson } = Query.people.byIdForMerge({
        id: selectedSecondPerson?.id,
        options: { enabled: selectedSecondPerson != null }
    });

    // All merge relation tables map to paid modules (attendance, tasks,
    // small groups, conversations, roles, blackout periods). Free orgs skip
    // the relations step entirely — no paid-only associations are sent.
    const relationTables = isFree ? {} : RELATION_TABLES;
    const stepSequence = isFree
        ? [STEP_SELECT, STEP_FIELDS, STEP_REVIEW]
        : [STEP_SELECT, STEP_FIELDS, STEP_RELATIONS, STEP_REVIEW];
    const stepLabels = ['Select', 'Fields', 'Relations', 'Review'];

    const customFieldsMap = useMemo(() => {
        return customFields?.reduce((acc, field) => ({
            ...acc,
            [field.id]: { label: field.name, type: field.type, icon: "document-text-outline" }
        }), {}) || {};
    }, [customFields]);

    const mergePeopleMutation = useMutation({
        mutationFn: mergePeople,
        onSuccess: () => {
            Toast.show({ type: 'success', text2: "The people have been merged successfully!" });
            queryClient.invalidateQueries({ queryKey: QueryKeys.people.byId(primaryPersonId) });
            queryClient.invalidateQueries({ queryKey: QueryKeys.people.potentialDuplicates() });
            resetForm();
            loader.disable();
        },
        onError: () => {
            loader.disable();
        }
    });

    const resetForm = useCallback(() => {
        setSelectedFirstPerson(null);
        setSelectedSecondPerson(null);
        setPrimaryPersonId(null);
        setFieldSelections({});
        setRelationSelections({});
        setCurrentStep(STEP_SELECT);
    }, []);

    const ignoreDuplicateMutation = useMutation({
        mutationFn: ignorePotentialDuplicate,
        onSuccess: (_, { person1ID, person2ID }) => {
            queryClient.setQueryData(QueryKeys.people.potentialDuplicates(), pairs => pairs?.filter(pair =>
                ![pair.person1.id, pair.person2.id].every(id => [person1ID, person2ID].includes(id))
            ));
            if ([selectedFirstPerson?.id, selectedSecondPerson?.id].every(id => [person1ID, person2ID].includes(id))) {
                resetForm();
            }
            queryClient.invalidateQueries({ queryKey: QueryKeys.people.potentialDuplicates() });
            Toast.show({ type: 'success', text2: 'Pair ignored. These profiles have not been changed.' });
        },
    });

    useEffect(() => {
        navigation.setOptions({
            headerRight: () => currentStep === STEP_REVIEW ? (
                <SolidButton
                    text="Merge"
                    iconName="git-merge-outline"
                    iconBefore
                    onPress={handleMerge}
                    size="sm"
                />
            ) : null
        });
    }, [currentStep, fieldSelections, relationSelections]);

    useEffect(() => {
        if (firstPerson && secondPerson) {
            if(!primaryPersonId) setPrimaryPersonId(firstPerson.id);
            initializeSelections();
        }
    }, [firstPerson, secondPerson]);

    const formatFieldValue = useCallback((field, person) => {
        if (!person || !field) return null;

        const isCustomField = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89abAB][0-9a-f]{3}-[0-9a-f]{12}$/i.test(field);
        const value = isCustomField ? person?.customFieldValues?.[field] : person[field];

        if (!value) return null;

        switch (field) {
            case "dateOfBirth":
                return formatMergeDate(value);
            case "phone":
                return Array.isArray(value) ? (value.length === 0 ? null : value.join(", ")) : value;
            case "Address":
                return value ? `${value.street}, ${value.city}, ${value.state} ${value.zip}` : null;
            case "InvitedBy": {
                const invited = Array.isArray(value) ? value[0] : value;
                return invited ? `${invited.firstName} ${invited.lastName}` : null;
            }
            default:
                if (isCustomField && customFieldsMap[field]) {
                    const dataType = customFieldsMap[field].type;
                    switch (dataType) {
                        case "Date":
                            return formatMergeDate(value);
                        case "Datetime":
                            return formatMergeDate(value, true);
                        case "Multiselect":
                            return Array.isArray(value) ? (value.length === 0 ? null : value.join(", ")) : String(value);
                        case "Checkbox":
                            return value ? "Yes" : "No";
                        case "File Upload": {
                            if (typeof value === 'string') return value;
                            const fileObj = Array.isArray(value) ? value[0] : value;
                            return fileObj?.name || fileObj?.filename || "File";
                        }
                        default:
                            return value;
                    }
                }
                return typeof value === "object" ? JSON.stringify(value) : value;
        }
    }, [customFieldsMap]);

    const formatRelationCount = useCallback((table, person) => {
        if (!person) return 0;

        const countMap = {
            "Attendance": person.Attendance?.[0]?.count || 0,
            "TaskInstancePeople": person.TaskInstancePeople?.[0]?.count || 0,
            "SmallGroupsLed": person.SmallGroupsAsLeader?.[0]?.count || 0,
            "PersonConversations": person.PersonConversations?.[0]?.count || 0,
            "RolePeople": person.RolePeople?.[0]?.count || 0,
            "BlackoutPeriods": person.BlackoutPeriods?.[0]?.count || 0,
        };

        return countMap[table] || 0;
    }, []);

    const initializeSelections = useCallback(() => {
        if (!firstPerson || !secondPerson) return;

        const initialFieldSelections = {};
        const initialRelationSelections = {};
        const primaryId = primaryPersonId || firstPerson.id;

        [...Object.keys(BASIC_FIELDS), ...Object.keys(customFieldsMap)].forEach(field => {
            const firstValue = formatFieldValue(field, firstPerson);
            const secondValue = formatFieldValue(field, secondPerson);

            if (firstValue && !secondValue) {
                initialFieldSelections[field] = firstPerson.id;
            } else if (!firstValue && secondValue) {
                initialFieldSelections[field] = secondPerson.id;
            } else {
                initialFieldSelections[field] = primaryId;
            }
        });

        Object.keys(relationTables).forEach(table => {
            const firstCount = formatRelationCount(table, firstPerson);
            const secondCount = formatRelationCount(table, secondPerson);

            if (relationTables[table].allowBoth) {
                initialRelationSelections[table] = 'both';
            } else if (firstCount > 0 && secondCount === 0) {
                initialRelationSelections[table] = firstPerson.id;
            } else if (secondCount > 0 && firstCount === 0) {
                initialRelationSelections[table] = secondPerson.id;
            } else {
                initialRelationSelections[table] = primaryId;
            }
        });

        setFieldSelections(initialFieldSelections);
        setRelationSelections(initialRelationSelections);
    }, [firstPerson, secondPerson, customFieldsMap, relationTables, primaryPersonId, formatFieldValue]);

    const handleMerge = useCallback(() => {
        showAlert({
            title: "Confirm Merge",
            subtitle: `This will merge the selected data into ${primaryPersonId === firstPerson?.id ? firstPerson?.firstName : secondPerson?.firstName}'s profile. The other person will be archived. This action cannot be undone.`,
            confirmText: "Merge People",
            onConfirm: () => {
                loader.enable();

                const personValues = { [firstPerson.id]: firstPerson, [secondPerson.id]: secondPerson };
                const newPerson = { customFieldValues: {}, associations: {}, fields: {} };

                Object.entries(fieldSelections).forEach(([field, personID]) => {
                    const isCustomField = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89abAB][0-9a-f]{3}-[0-9a-f]{12}$/i.test(field);
                    const value = isCustomField
                        ? personValues[personID]?.customFieldValues?.[field]
                        : personValues[personID][field];

                    if (value) {
                        switch (field) {
                            case 'Address':
                                newPerson.fields["addressID"] = personValues[personID].addressID;
                                break;
                            case 'InvitedBy':
                                newPerson.fields["invitedByID"] = personValues[personID].invitedByID;
                                break;
                            default:
                                if (isCustomField) {
                                    newPerson.customFieldValues[field] = value;
                                } else {
                                    newPerson.fields[field] = value;
                                }
                        }
                    }
                });

                Object.entries(relationSelections).forEach(([table, selection]) => {
                    newPerson.associations[table] = selection;
                });

                const keepPerson = primaryPersonId === firstPerson.id ? firstPerson : secondPerson;
                const archivePerson = primaryPersonId === firstPerson.id ? secondPerson : firstPerson;

                mergePeopleMutation.mutate({
                    newPerson: { ...newPerson.fields, customFieldValues: newPerson.customFieldValues },
                    associations: newPerson.associations,
                    firstPerson: keepPerson,
                    secondPerson: archivePerson
                });
            }
        });
    }, [fieldSelections, relationSelections, primaryPersonId, firstPerson, secondPerson]);

    const canProceedToNextStep = useMemo(() => {
        switch (currentStep) {
            case STEP_SELECT:
                return selectedFirstPerson && selectedSecondPerson && selectedFirstPerson.id !== selectedSecondPerson?.id;
            case STEP_FIELDS:
            case STEP_RELATIONS:
            case STEP_REVIEW:
                return true;
            default:
                return false;
        }
    }, [currentStep, selectedFirstPerson, selectedSecondPerson]);

    const isLoading = loadingFirstPerson || loadingSecondPerson;

    const renderStepIndicator = () => (
        <View style={styles.stepIndicatorContainer}>
            <ScrollView horizontal showsHorizontalScrollIndicator={false} keyboardShouldPersistTaps="handled"
>
                {stepSequence.map((step, index) => {
                const label = stepLabels[step];
                const visited = stepSequence.indexOf(currentStep) > index;
                return (
                <Pressable
                    key={label}
                    style={styles.stepItem}
                    onPress={() => visited && setCurrentStep(step)}
                    disabled={!visited}
                >
                    <View style={styles.stepCircle(currentStep === step, visited)}>
                        {visited ? (
                            <Unicon name="checkmark" size={14} colorVariant="onPrimary" />
                        ) : (
                            <Text style={styles.stepNumber(currentStep === step)}>{index + 1}</Text>
                        )}
                    </View>
                    <Text style={styles.stepLabel(currentStep === step)}>{label}</Text>
                    {index < stepSequence.length - 1 && <View style={styles.stepConnector(visited)} />}
                </Pressable>
                );
            })}
            </ScrollView>
        </View>
    );

    const renderPersonCard = (person) => {
        if (!person) return null;
        const isPrimary = primaryPersonId === person.id;

        return (
            <Pressable
                style={styles.personCard(isPrimary)}
                onPress={() => setPrimaryPersonId(person.id)}
            >
                <View style={styles.personCardHeader}>
                    <Avatar profile={person} size={50} />
                    <View style={styles.personCardInfo}>
                        <Text style={styles.personName}>{person.firstName} {person.lastName}</Text>
                        <Text style={styles.personEmail}>{person.email || 'No email'}</Text>
                    </View>
                    {isPrimary && (
                        <View style={styles.primaryBadge}>
                            <Unicon name="star" size={12} colorVariant="warning" />
                            <Text style={styles.primaryBadgeText}>Primary</Text>
                        </View>
                    )}
                </View>
                <View style={styles.personCardFooter}>
                    <Unicon name={isPrimary ? "checkmark-circle" : "ellipse-outline"} size={20} colorVariant={isPrimary ? "success" : "textSecondary"} />
                    <Text style={styles.personCardFooterText}>
                        {isPrimary ? "This person's profile will be kept" : "Tap to make primary"}
                    </Text>
                </View>
            </Pressable>
        );
    };

    const renderFieldComparison = (fieldKey, fieldConfig) => {
        const firstValue = formatFieldValue(fieldKey, firstPerson);
        const secondValue = formatFieldValue(fieldKey, secondPerson);
        const isSame = firstValue === secondValue;
        const selectedPersonId = fieldSelections[fieldKey];

        if (isSame && !firstValue) return null;

        return (
            <View key={fieldKey} style={styles.fieldComparisonContainer}>
                <View style={styles.fieldHeader}>
                    <Unicon name={fieldConfig.icon || "document-text-outline"} size={16} colorVariant="textSecondary" />
                    <Text style={styles.fieldLabel}>{fieldConfig.label}</Text>
                    {isSame && <View style={styles.sameValueBadge}><Text style={styles.sameValueText}>Same</Text></View>}
                </View>

                <View style={styles.fieldOptionsRow}>
                    <Pressable
                        style={styles.fieldOption(selectedPersonId === firstPerson?.id, isSame)}
                        onPress={() => !isSame && setFieldSelections(prev => ({ ...prev, [fieldKey]: firstPerson.id }))}
                        disabled={isSame}
                    >
                        <Text style={styles.fieldOptionValue(!!firstValue)} numberOfLines={2}>
                            {firstValue || "No value"}
                        </Text>
                        <Unicon
                            name={selectedPersonId === firstPerson?.id ? "checkmark-circle" : "ellipse-outline"}
                            size={18}
                            colorVariant={selectedPersonId === firstPerson?.id ? "success" : "textSecondary"}
                        />
                    </Pressable>

                    <Pressable
                        style={styles.fieldOption(selectedPersonId === secondPerson?.id, isSame)}
                        onPress={() => !isSame && setFieldSelections(prev => ({ ...prev, [fieldKey]: secondPerson.id }))}
                        disabled={isSame}
                    >
                        <Text style={styles.fieldOptionValue(!!secondValue)} numberOfLines={2}>
                            {secondValue || "No value"}
                        </Text>
                        <Unicon
                            name={selectedPersonId === secondPerson?.id ? "checkmark-circle" : "ellipse-outline"}
                            size={18}
                            colorVariant={selectedPersonId === secondPerson?.id ? "success" : "textSecondary"}
                        />
                    </Pressable>
                </View>
            </View>
        );
    };

    const renderRelationComparison = (tableKey, tableConfig) => {
        const firstCount = formatRelationCount(tableKey, firstPerson);
        const secondCount = formatRelationCount(tableKey, secondPerson);
        const selection = relationSelections[tableKey];

        return (
            <View key={tableKey} style={styles.relationContainer}>
                <View style={styles.relationHeader}>
                    <View style={styles.relationTitleRow}>
                        <Unicon name={tableConfig.icon} size={18} colorVariant="primary" />
                        <Text style={styles.relationTitle}>{tableConfig.label}</Text>
                    </View>
                    <Text style={styles.relationDescription}>{tableConfig.description}</Text>
                </View>

                <View style={styles.relationCountsRow}>
                    <View style={styles.relationCountBox}>
                        <Text style={styles.relationCountLabel}>{firstPerson?.firstName} {firstPerson?.lastName}</Text>
                        <Text style={styles.relationCountSubLabel}>{firstPerson?.email || `ID: ${firstPerson?.id?.slice(0, 8)}`}</Text>
                        <Text style={styles.relationCountValue}>{firstCount}</Text>
                    </View>
                    <View style={styles.relationCountBox}>
                        <Text style={styles.relationCountLabel}>{secondPerson?.firstName} {secondPerson?.lastName}</Text>
                        <Text style={styles.relationCountSubLabel}>{secondPerson?.email || `ID: ${secondPerson?.id?.slice(0, 8)}`}</Text>
                        <Text style={styles.relationCountValue}>{secondCount}</Text>
                    </View>
                </View>

                <View style={styles.relationOptionsRow}>
                    <Pressable
                        style={styles.relationOption(selection === firstPerson?.id)}
                        onPress={() => setRelationSelections(prev => ({ ...prev, [tableKey]: firstPerson.id }))}
                    >
                        <Unicon
                            name={selection === firstPerson?.id ? "checkmark-circle" : "ellipse-outline"}
                            size={16}
                            colorVariant={selection === firstPerson?.id ? "success" : "textSecondary"}
                        />
                        <View style={styles.relationOptionTextContainer}>
                            <Text style={styles.relationOptionText(selection === firstPerson?.id)}>
                                Keep {firstPerson?.firstName}
                            </Text>
                            <Text style={styles.relationOptionSubText}>{firstPerson?.email || firstPerson?.lastName}</Text>
                        </View>
                    </Pressable>

                    {tableConfig.allowBoth && (
                        <Pressable
                            style={styles.relationOption(selection === 'both')}
                            onPress={() => setRelationSelections(prev => ({ ...prev, [tableKey]: 'both' }))}
                        >
                            <Unicon
                                name={selection === 'both' ? "checkmark-circle" : "ellipse-outline"}
                                size={16}
                                colorVariant={selection === 'both' ? "success" : "textSecondary"}
                            />
                            <Text style={styles.relationOptionText(selection === 'both')}>Keep Both</Text>
                        </Pressable>
                    )}

                    <Pressable
                        style={styles.relationOption(selection === secondPerson?.id)}
                        onPress={() => setRelationSelections(prev => ({ ...prev, [tableKey]: secondPerson.id }))}
                    >
                        <Unicon
                            name={selection === secondPerson?.id ? "checkmark-circle" : "ellipse-outline"}
                            size={16}
                            colorVariant={selection === secondPerson?.id ? "success" : "textSecondary"}
                        />
                        <View style={styles.relationOptionTextContainer}>
                            <Text style={styles.relationOptionText(selection === secondPerson?.id)}>
                                Keep {secondPerson?.firstName}
                            </Text>
                            <Text style={styles.relationOptionSubText}>{secondPerson?.email || secondPerson?.lastName}</Text>
                        </View>
                    </Pressable>
                </View>

                {!tableConfig.allowBoth && (
                    <View style={styles.relationWarning}>
                        <Unicon name="information-circle-outline" size={14} colorVariant="warning" />
                        <Text style={styles.relationWarningText}>
                            Cannot keep both due to unique constraints
                        </Text>
                    </View>
                )}
            </View>
        );
    };

    const getPersonIdentifier = (person) => {
        if (!person) return '';
        if (person.email) return person.email;
        return `${person.firstName} ${person.lastName}`;
    };

    const renderReviewSection = () => {
        const keepPerson = primaryPersonId === firstPerson?.id ? firstPerson : secondPerson;
        const archivePerson = primaryPersonId === firstPerson?.id ? secondPerson : firstPerson;

        const fieldChanges = Object.entries(fieldSelections).filter(([field, personId]) => {
            const firstValue = formatFieldValue(field, firstPerson);
            const secondValue = formatFieldValue(field, secondPerson);
            return firstValue !== secondValue && personId !== primaryPersonId;
        });

        return (
            <View style={styles.reviewContainer}>
                <Callout
                    type="warning"
                    title="Review Carefully"
                    message="Please review the merge summary below. This action cannot be undone."
                />

                <View style={styles.reviewSection}>
                    <SectionTitle title="Primary Person (Will be kept)" />
                    {renderPersonCard(keepPerson)}
                </View>

                <View style={styles.reviewSection}>
                    <SectionTitle title="Secondary Person (Will be archived)" />
                    <View style={styles.archivePersonCard}>
                        <Avatar profile={archivePerson} size={40} />
                        <View style={styles.archivePersonInfo}>
                            <Text style={styles.archivePersonName}>{archivePerson?.firstName} {archivePerson?.lastName}</Text>
                            <Text style={styles.archivePersonEmail}>{archivePerson?.email || 'No email'}</Text>
                            <Text style={styles.archivePersonNote}>This profile will be archived after merge</Text>
                        </View>
                    </View>
                </View>

                {fieldChanges.length > 0 && (
                    <View style={styles.reviewSection}>
                        <SectionTitle title={`Field Values from ${getPersonIdentifier(archivePerson)}`} />
                        <View style={styles.changesList}>
                            {fieldChanges.map(([field]) => {
                                const config = BASIC_FIELDS[field] || customFieldsMap[field];
                                return (
                                    <View key={field} style={styles.changeItem}>
                                        <Unicon name="arrow-forward" size={14} colorVariant="primary" />
                                        <Text style={styles.changeItemText}>{config?.label || field}</Text>
                                    </View>
                                );
                            })}
                        </View>
                    </View>
                )}

                {Object.keys(relationTables).length > 0 && (
                <View style={styles.reviewSection}>
                    <SectionTitle title="Related Records" />
                    <View style={styles.changesList}>
                        {Object.entries(relationSelections).map(([table, selection]) => {
                            const config = relationTables[table];
                            if (!config) return null;
                            const action = selection === 'both' ? 'Merge both' :
                                selection === firstPerson?.id ? `Keep ${getPersonIdentifier(firstPerson)}` :
                                `Keep ${getPersonIdentifier(secondPerson)}`;
                            return (
                                <View key={table} style={styles.changeItem}>
                                    <Unicon name={config.icon} size={14} colorVariant="textSecondary" />
                                    <Text style={styles.changeItemText}>{config.label}: {action}</Text>
                                </View>
                            );
                        })}
                    </View>
                </View>
                )}
            </View>
        );
    };

    const renderDuplicatePairs = () => {
        if (loadingDuplicates) {
            return (
                <View style={styles.duplicatesLoading}>
                    <ActivityIndicator size="small" />
                    <Text style={styles.duplicatesLoadingText}>Scanning for duplicates...</Text>
                </View>
            );
        }

        if (duplicatesError) {
            return (
                <View style={styles.duplicatesLoading}>
                    <Text style={styles.duplicatesLoadingText}>Could not load duplicate suggestions.</Text>
                    <SolidButton text="Try again" testID="merge-duplicates-retry" variant="muted" size="sm" onPress={refetchDuplicates} />
                </View>
            );
        }

        if (!potentialDuplicates?.length) {
            return (
                <View style={styles.duplicatesEmpty}>
                    <Unicon name="checkmark-circle-outline" size={28} colorVariant="success" />
                    <View style={styles.emptyText}>
                        <Text style={styles.sectionTitle}>No potential duplicates found</Text>
                        <Text style={styles.sectionDescription}>All caught up. You can still choose two people manually.</Text>
                    </View>
                </View>
            );
        }

        return (
            <View style={styles.duplicateListContainer}>
                {potentialDuplicates.map(pair => (
                    <DuplicatePairCard
                        key={`${pair.person1.id}-${pair.person2.id}`}
                        pair={pair}
                        selected={[selectedFirstPerson?.id, selectedSecondPerson?.id].every(id => [pair.person1.id, pair.person2.id].includes(id))}
                        processing={ignoreDuplicateMutation.isPending && ignoreDuplicateMutation.variables?.person1ID === pair.person1.id
                            && ignoreDuplicateMutation.variables?.person2ID === pair.person2.id}
                        disabled={ignoreDuplicateMutation.isPending}
                        onReview={() => {
                            setSelectedFirstPerson(pair.person1);
                            setSelectedSecondPerson(pair.person2);
                            setPrimaryPersonId(pair.person1.id);
                            scrollRef.current?.scrollTo({ y: 0, animated: true });
                        }}
                        onIgnore={() => ignoreDuplicateMutation.mutate({ person1ID: pair.person1.id, person2ID: pair.person2.id })}
                    />
                ))}
            </View>
        );
    };

    const renderStepContent = () => {
        switch (currentStep) {
            case STEP_SELECT:
                return (
                    <View style={styles.stepContent}>
                        <View style={styles.intro}>
                            <View style={styles.introIcon}><Unicon name="git-merge-outline" size={24} colorVariant="people" /></View>
                            <View style={styles.introText}>
                                <Text style={styles.introTitle}>One person. One profile.</Text>
                                <Text style={styles.sectionDescription}>Review a suggested pair or choose two people below. Nothing changes until you confirm the merge.</Text>
                            </View>
                        </View>
                        <InputSection
                            title="Choose people"
                            description="Select records manually, or use Review pair from the suggestions below."
                        >
                            <View style={styles.selectorsRow}>
                                <View style={styles.selectorHalf}>
                                    <SheetSelector
                                        type="people"
                                        title="First Person"
                                        fromScreen={pathname}
                                        value={selectedFirstPerson}
                                        excludeIds={selectedSecondPerson ? [selectedSecondPerson.id] : []}
                                        onChange={(item) => {
                                            setSelectedFirstPerson(item);
                                            if (item && !primaryPersonId) setPrimaryPersonId(item.id);
                                        }}
                                    />
                                </View>
                                <View style={styles.selectorHalf}>
                                    <SheetSelector
                                        type="people"
                                        title="Second Person"
                                        fromScreen={pathname}
                                        value={selectedSecondPerson}
                                        excludeIds={selectedFirstPerson ? [selectedFirstPerson.id] : []}
                                        onChange={setSelectedSecondPerson}
                                    />
                                </View>
                            </View>
                        </InputSection>

                        {selectedFirstPerson?.id === selectedSecondPerson?.id && selectedFirstPerson && (
                            <Callout type="danger" message="You cannot merge a person with themselves. Please select two different people." />
                        )}

                        {isLoading && (
                            <View style={styles.loadingContainer}>
                                <ActivityIndicator size="large" />
                                <Text style={styles.loadingText}>Loading person details...</Text>
                            </View>
                        )}

                        {firstPerson && secondPerson && !isLoading && (
                            <InputSection title="Choose Primary Person" description="Select which person's profile should be kept. The other person will be archived.">
                                {renderPersonCard(firstPerson, true)}
                                {renderPersonCard(secondPerson, false)}
                            </InputSection>
                        )}

                        <View style={styles.duplicatesSection} testID="merge-potential-duplicates">
                            <View style={styles.duplicatesHeader}>
                                <View style={styles.introText}>
                                    <Text style={styles.sectionTitle}>Potential duplicates</Text>
                                    <Text style={styles.sectionDescription}>Click a pair to review. Ignore hides that suggestion for everyone in your organization, without changing either profile.</Text>
                                </View>
                                {!loadingDuplicates && !duplicatesError && (
                                    <View style={styles.countBadge}>
                                        <Text style={styles.countText} testID="merge-duplicates-count">{potentialDuplicates?.length || 0}</Text>
                                    </View>
                                )}
                            </View>
                            {renderDuplicatePairs()}
                        </View>
                    </View>
                );

            case STEP_FIELDS:
                return (
                    <View style={styles.stepContent}>
                        <Callout
                            type="info"
                            message="For each field with different values, choose which value to keep in the merged profile."
                        />

                        <InputSection title="Basic Information">
                            {Object.entries(BASIC_FIELDS).map(([key, config]) =>
                                renderFieldComparison(key, config)
                            )}
                        </InputSection>

                        {Object.keys(customFieldsMap).length > 0 && (
                            <InputSection title="Custom Fields">
                                {Object.entries(customFieldsMap).map(([key, config]) =>
                                    renderFieldComparison(key, config, true)
                                )}
                            </InputSection>
                        )}
                    </View>
                );

            case STEP_RELATIONS:
                return (
                    <View style={styles.stepContent}>
                        <Callout
                            type="info"
                            message="Choose how to handle related records. Some records can be merged, while others must be kept from one person only due to database constraints."
                        />

                        <InputSection title="Related Records">
                            {Object.entries(relationTables).map(([key, config]) =>
                                renderRelationComparison(key, config)
                            )}
                        </InputSection>
                    </View>
                );

            case STEP_REVIEW:
                return (
                    <View style={styles.stepContent}>
                        {renderReviewSection()}
                    </View>
                );

            default:
                return null;
        }
    };

    return (
        <View style={styles.container}>
            {renderStepIndicator()}

            <ScrollView
                ref={scrollRef}
                style={styles.scrollView}
                contentContainerStyle={styles.scrollContent}
                showsVerticalScrollIndicator={false}
                contentInsetAdjustmentBehavior="automatic"
                keyboardShouldPersistTaps="handled"
            >
                {renderStepContent()}
            </ScrollView>

            <View style={styles.navigationBar}>
                <View style={styles.navButtonContainer}>
                    {currentStep > STEP_SELECT && (
                        <SolidButton
                            text="Back"
                            iconName="arrow-back"
                            iconBefore
                            variant="outlined"
                            onPress={() => setCurrentStep(stepSequence[Math.max(stepSequence.indexOf(currentStep) - 1, 0)])}
                        />
                    )}
                </View>

                <View style={styles.navButtonContainer}>
                    {currentStep < STEP_REVIEW ? (
                        <SolidButton
                            text="Continue"
                            testID="merge-continue"
                            iconName="arrow-forward"
                            disabled={!canProceedToNextStep || isLoading}
                            onPress={() => setCurrentStep(stepSequence[Math.min(stepSequence.indexOf(currentStep) + 1, stepSequence.length - 1)])}
                        />
                    ) : (
                        <SolidButton
                            text="Merge People"
                            iconName="git-merge-outline"
                            iconBefore
                            type="success"
                            onPress={handleMerge}
                            processing={mergePeopleMutation.isPending}
                        />
                    )}
                </View>
            </View>
        </View>
    );
}

const styles = StyleSheet.create(({ colors, fonts, shadows, spacing, typography }, rt) => ({
    container: {
        flex: 1,
        backgroundColor: 'transparent',
        paddingBottom: Platform.OS === 'ios' && parseInt(Platform.Version, 10) >= 26 && !Platform.isPad && !rt.isLandscape ? 80 : 0,
    },
    scrollView: {
        flex: 1,
    },
    scrollContent: {
        padding: { xs: spacing.md, md: spacing.lg },
        paddingBottom: spacing.xl,
        width: '100%',
        maxWidth: 1040,
        alignSelf: 'center',
    },
    stepContent: {
        gap: spacing.lg,
    },
    section: {
        backgroundColor: colors.surface,
        borderRadius: 4,
        padding: spacing.md,
        gap: spacing.md,
        boxShadow: shadows.card,
    },
    sectionHeader: {
        gap: spacing.sm,
        paddingBottom: spacing.md,
        borderBottomWidth: 1,
        borderBottomColor: colors.borderSubtle,
    },
    sectionTitle: { fontFamily: fonts.bold, fontSize: typography.body, color: colors.text, letterSpacing: -0.2 },
    sectionDescription: { fontFamily: fonts.body, fontSize: typography.caption, lineHeight: 20, color: colors.textSecondary },
    sectionBody: { gap: spacing.sm },
    intro: { flexDirection: 'row', gap: spacing.md, alignItems: 'flex-start', paddingVertical: spacing.sm },
    introIcon: {
        width: 48, height: 48, borderRadius: 12, backgroundColor: colors.peopleContainer,
        alignItems: 'center', justifyContent: 'center',
    },
    introText: { flex: 1, minWidth: 0, gap: spacing.sm },
    introTitle: { fontFamily: fonts.heading, fontSize: typography.h1, color: colors.text, letterSpacing: -0.5 },
    duplicatesSection: { gap: spacing.md },
    duplicatesHeader: { flexDirection: 'row', gap: spacing.md, alignItems: 'flex-start' },
    countBadge: {
        minWidth: 32, paddingHorizontal: spacing.sm, paddingVertical: spacing.xs,
        backgroundColor: colors.peopleContainer, borderRadius: 12, alignItems: 'center',
    },
    countText: { fontFamily: fonts.bold, fontSize: typography.caption, color: colors.people, fontVariant: ['tabular-nums'] },
    emptyText: { flex: 1, gap: spacing.xs },
    stepIndicatorContainer: {
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center',
        paddingVertical: 16,
        paddingHorizontal: 16,
        backgroundColor: colors.surface,
        borderBottomWidth: 1,
        borderBottomColor: colors.borderSubtle,
    },
    stepItem: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    stepCircle: (isActive, isCompleted) => ({
        width: 28,
        height: 28,
        borderRadius: 14,
        backgroundColor: isCompleted ? colors.people : isActive ? colors.people : colors.surfaceVariant,
        justifyContent: 'center',
        alignItems: 'center',
    }),
    stepNumber: (isActive) => ({
        color: isActive ? colors.onPrimary : colors.textSecondary,
        fontSize: 12,
        fontFamily: fonts.bold,
    }),
    stepLabel: (isActive) => ({
        marginLeft: 6,
        fontSize: 12,
        color: isActive ? colors.people : colors.textSecondary,
        fontFamily: isActive ? fonts.bold : fonts.body,
    }),
    stepConnector: (isCompleted) => ({
        width: rt.screen.width > 500 ? 40 : 20,
        height: 2,
        backgroundColor: isCompleted ? colors.people : colors.borderSubtle,
        marginHorizontal: 8,
    }),
    personCard: (isPrimary) => ({
        backgroundColor: colors.surface,
        borderRadius: 12,
        padding: 16,
        marginVertical: 8,
        borderWidth: 2,
        borderColor: isPrimary ? colors.success : colors.border,
        boxShadow: shadows.card,
    }),
    personCardHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
    },
    personCardInfo: {
        flex: 1,
    },
    personName: {
        fontSize: 16,
        fontFamily: fonts.bold,
        color: colors.text,
    },
    personEmail: {
        fontSize: 13,
        color: colors.textSecondary,
        marginTop: 2,
    },
    primaryBadge: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: colors.warningContainer,
        paddingHorizontal: 8,
        paddingVertical: 4,
        borderRadius: 12,
        gap: 4,
    },
    primaryBadgeText: {
        fontSize: 11,
        color: colors.warning,
        fontFamily: fonts.bold,
    },
    personCardFooter: {
        flexDirection: 'row',
        alignItems: 'center',
        marginTop: 12,
        paddingTop: 12,
        borderTopWidth: 1,
        borderTopColor: colors.border,
        gap: 8,
    },
    personCardFooterText: {
        fontSize: 13,
        color: colors.textSecondary,
    },
    fieldComparisonContainer: {
        marginVertical: 8,
    },
    fieldHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
        marginBottom: 8,
    },
    fieldLabel: {
        fontSize: 13,
        fontFamily: fonts.bold,
        color: colors.text,
        flex: 1,
    },
    sameValueBadge: {
        backgroundColor: colors.border,
        paddingHorizontal: 8,
        paddingVertical: 2,
        borderRadius: 10,
    },
    sameValueText: {
        fontSize: 10,
        color: colors.textSecondary,
        fontFamily: fonts.bold,
    },
    fieldOptionsRow: {
        flexDirection: rt.screen.width > 500 ? 'row' : 'column',
        gap: 10,
    },
    fieldOption: (isSelected, isSame) => ({
        flex: rt.screen.width > 500 ? 1 : undefined,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: 12,
        borderRadius: 8,
        borderWidth: 1,
        borderColor: isSelected ? colors.success : colors.border,
        backgroundColor: isSame ? colors.background : colors.surface,
        opacity: isSame ? 0.6 : 1,
    }),
    fieldOptionValue: (hasValue) => ({
        flex: 1,
        fontSize: 13,
        color: hasValue ? colors.text : colors.textSecondary,
        fontStyle: hasValue ? 'normal' : 'italic',
        marginRight: 8,
    }),
    relationContainer: {
        backgroundColor: colors.surface,
        borderRadius: 12,
        padding: 16,
        marginVertical: 8,
        borderWidth: 1,
        borderColor: colors.border,
    },
    relationHeader: {
        marginBottom: 12,
    },
    relationTitleRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    relationTitle: {
        fontSize: 15,
        fontFamily: fonts.bold,
        color: colors.text,
    },
    relationDescription: {
        fontSize: 12,
        color: colors.textSecondary,
        marginTop: 4,
        marginLeft: 26,
    },
    relationCountsRow: {
        flexDirection: 'row',
        justifyContent: 'center',
        gap: 12,
        marginBottom: 12,
    },
    relationCountBox: {
        flex: 1,
        maxWidth: 300,
        backgroundColor: colors.background,
        padding: 12,
        borderRadius: 8,
        alignItems: 'center',
    },
    relationCountLabel: {
        fontSize: 12,
        fontFamily: fonts.bold,
        color: colors.text,
        marginBottom: 2,
        textAlign: 'center',
    },
    relationCountSubLabel: {
        fontSize: 10,
        color: colors.textSecondary,
        marginBottom: 6,
        textAlign: 'center',
    },
    relationCountValue: {
        fontSize: 20,
        fontFamily: fonts.bold,
        color: colors.primary,
    },
    relationOptionsRow: {
        flexDirection: rt.screen.width > 500 ? 'row' : 'column',
        justifyContent: 'center',
        gap: 8,
    },
    relationOption: (isSelected) => ({
        flex: rt.screen.width > 500 ? 1 : undefined,
        maxWidth: rt.screen.width > 500 ? 200 : undefined,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6,
        padding: 10,
        borderRadius: 8,
        borderWidth: 1,
        borderColor: isSelected ? colors.success : colors.border,
        backgroundColor: isSelected ? colors.successContainer : colors.background,
    }),
    relationOptionTextContainer: {
        alignItems: 'center',
    },
    relationOptionText: (isSelected) => ({
        fontSize: 12,
        color: isSelected ? colors.success : colors.textSecondary,
        fontFamily: isSelected ? fonts.bold : fonts.body,
    }),
    relationOptionSubText: {
        fontSize: 10,
        color: colors.textSecondary,
    },
    relationWarning: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        marginTop: 10,
        paddingTop: 10,
        borderTopWidth: 1,
        borderTopColor: colors.border,
    },
    relationWarningText: {
        fontSize: 11,
        color: colors.warning,
        fontStyle: 'italic',
    },
    reviewContainer: {
        gap: 16,
    },
    reviewSection: {
        marginTop: 8,
    },
    archivePersonCard: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: colors.surface,
        padding: 12,
        borderRadius: 8,
        borderWidth: 1,
        borderColor: colors.border,
        borderStyle: 'dashed',
        gap: 12,
        opacity: 0.8,
    },
    archivePersonInfo: {
        flex: 1,
    },
    archivePersonName: {
        fontSize: 14,
        fontFamily: fonts.bold,
        color: colors.text,
    },
    archivePersonEmail: {
        fontSize: 12,
        color: colors.textSecondary,
        marginBottom: 2,
    },
    archivePersonNote: {
        fontSize: 11,
        color: colors.textSecondary,
        fontStyle: 'italic',
    },
    changesList: {
        backgroundColor: colors.surface,
        borderRadius: 8,
        padding: 12,
        gap: 8,
    },
    changeItem: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    changeItemText: {
        fontSize: 13,
        color: colors.text,
    },
    navigationBar: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: 16,
        backgroundColor: colors.surface,
        borderTopWidth: 1,
        borderTopColor: colors.border,
        boxShadow: shadows.card,
    },
    navButtonContainer: {
        minWidth: 100,
    },
    loadingContainer: {
        alignItems: 'center',
        justifyContent: 'center',
        padding: 40,
        gap: 12,
    },
    loadingText: {
        fontSize: 14,
        color: colors.textSecondary,
    },
    duplicatesLoading: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 10,
        padding: 14,
        flexWrap: 'wrap',
    },
    duplicatesLoadingText: {
        fontSize: 13,
        color: colors.textSecondary,
    },
    duplicatesEmpty: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: spacing.md,
        padding: spacing.lg,
        borderRadius: 8,
        backgroundColor: colors.surface,
        boxShadow: shadows.card,
    },
    duplicatesEmptyText: {
        fontSize: 13,
        color: colors.textSecondary,
    },
    selectorsRow: {
        flexDirection: { xs: 'column', md: 'row' },
        gap: 12,
    },
    selectorHalf: {
        flex: 1,
        minWidth: 0,
    },
    duplicateListContainer: {
        gap: spacing.md,
        padding: spacing.xs,
    },
}))